import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ItemEditor from '../components/ItemEditor';
import FormField from '../components/FormField';
import Modal from '../components/Modal';
import PaymentModal from '../components/PaymentModal';

interface InvoiceItem {
  id?: number;
  item_name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  amount: number;
  sort_order: number;
}

interface InvoiceFormData {
  order_id: number | null;
  title: string;
  issue_date: string;
  payment_deadline: string;
  billing_round: number;
  discount: number;
  tax_rate: number;
  tax_rounding: 'floor' | 'round' | 'ceil';
  items: InvoiceItem[];
  paid_amount: number;
}

const defaultFormData: InvoiceFormData = {
  order_id: null,
  title: '',
  issue_date: new Date().toISOString().split('T')[0],
  payment_deadline: '',
  billing_round: 1,
  discount: 0,
  tax_rate: 10,
  tax_rounding: 'floor',
  items: [],
  paid_amount: 0,
};

const InvoiceEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromOrderId = searchParams.get('fromOrder');
  const isNew = !id || id === 'new';

  const [formData, setFormData] = useState<InvoiceFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      loadInvoice(Number(id));
    } else if (fromOrderId) {
      loadFromOrder(Number(fromOrderId));
    }
  }, [id, fromOrderId]);

  const loadInvoice = async (invoiceId: number) => {
    setLoading(true);
    try {
      const invoice = await window.api.invoice.get(invoiceId);
      setFormData({
        order_id: invoice.order_id,
        title: invoice.title,
        issue_date: invoice.issue_date,
        payment_deadline: invoice.payment_deadline,
        billing_round: invoice.billing_round || 1,
        discount: invoice.discount || 0,
        tax_rate: invoice.tax_rate || 10,
        tax_rounding: invoice.tax_rounding || 'floor',
        items: invoice.items || [],
        paid_amount: invoice.paid_amount || 0,
      });
    } catch (err) {
      setError('請求書の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadFromOrder = async (orderId: number) => {
    setLoading(true);
    try {
      const order = await window.api.order.get(orderId);
      setFormData({
        order_id: orderId,
        title: order.title,
        issue_date: new Date().toISOString().split('T')[0],
        payment_deadline: '',
        billing_round: 1,
        discount: order.discount || 0,
        tax_rate: order.tax_rate || 10,
        tax_rounding: order.tax_rounding || 'floor',
        items: order.items || [],
        paid_amount: 0,
      });
    } catch (err) {
      setError('注文請書の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleItemsChange = useCallback((items: InvoiceItem[]) => {
    setFormData(prev => ({ ...prev, items }));
  }, []);

  const calculateTotals = useCallback(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const afterDiscount = subtotal - (formData.discount || 0);
    let taxAmount: number;
    const taxRate = formData.tax_rate / 100;
    const rawTax = afterDiscount * taxRate;
    switch (formData.tax_rounding) {
      case 'floor':
        taxAmount = Math.floor(rawTax);
        break;
      case 'ceil':
        taxAmount = Math.ceil(rawTax);
        break;
      case 'round':
        taxAmount = Math.round(rawTax);
        break;
      default:
        taxAmount = Math.floor(rawTax);
    }
    const total = afterDiscount + taxAmount;
    const unpaidAmount = total - (formData.paid_amount || 0);
    return { subtotal, afterDiscount, taxAmount, total, unpaidAmount };
  }, [formData.items, formData.discount, formData.tax_rate, formData.tax_rounding, formData.paid_amount]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = {
        ...formData,
      };
      if (isNew) {
        const result = await window.api.invoice.create(data);
        navigate(`/invoice/${result.id}`);
      } else {
        await window.api.invoice.update(Number(id), data);
      }
    } catch (err) {
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await window.api.invoice.delete(Number(id));
      navigate('/invoices');
    } catch (err) {
      setError('削除に失敗しました');
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!id && isNew) {
      setError('先に保存してください');
      return;
    }
    try {
      await window.api.invoice.generatePdf(Number(id));
    } catch (err) {
      setError('PDF生成に失敗しました');
    }
  };

  const handlePaymentRecord = async (amount: number, date: string, note: string) => {
    if (!id) return;
    try {
      await window.api.invoice.recordPayment(Number(id), amount, date, note);
      await loadInvoice(Number(id));
      setShowPaymentModal(false);
    } catch (err) {
      setError('入金記録に失敗しました');
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        読み込み中...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>
          {isNew ? '新規請求書' : '請求書編集'}
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleGeneratePdf}
            disabled={isNew}
            style={{
              padding: '8px 16px',
              backgroundColor: isNew ? '#ccc' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isNew ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            PDF出力
          </button>
          <button
            onClick={() => setShowPaymentModal(true)}
            disabled={isNew}
            style={{
              padding: '8px 16px',
              backgroundColor: isNew ? '#ccc' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isNew ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            入金記録
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 16px',
              backgroundColor: saving ? '#ccc' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {saving ? '保存中...' : '保存'}
          </button>
          {!isNew && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              削除
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>請求金額</div>
        <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#2563eb' }}>
          ¥{totals.total.toLocaleString()}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginTop: '12px', fontSize: '14px', color: '#666' }}>
          <span>入金済: ¥{formData.paid_amount.toLocaleString()}</span>
          <span style={{ color: totals.unpaidAmount > 0 ? '#dc2626' : '#059669' }}>
            未収額: ¥{totals.unpaidAmount.toLocaleString()}
          </span>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {formData.order_id && (
            <FormField label="注文請書">
              <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '14px', color: '#666' }}>
                注文請書 #{formData.order_id}
              </div>
            </FormField>
          )}

          <FormField label="件名" required>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="発行日">
            <input
              type="date"
              value={formData.issue_date}
              onChange={(e) => handleFieldChange('issue_date', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="支払期限">
            <input
              type="date"
              value={formData.payment_deadline}
              onChange={(e) => handleFieldChange('payment_deadline', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="第何回請求">
            <input
              type="number"
              value={formData.billing_round}
              onChange={(e) => handleFieldChange('billing_round', Number(e.target.value))}
              min={1}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>明細</h2>
        <ItemEditor items={formData.items} onChange={handleItemsChange} />
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <FormField label="値引">
            <input
              type="number"
              value={formData.discount}
              onChange={(e) => handleFieldChange('discount', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                textAlign: 'right',
              }}
            />
          </FormField>

          <FormField label="税率">
            <select
              value={formData.tax_rate}
              onChange={(e) => handleFieldChange('tax_rate', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value={10}>10%</option>
              <option value={8}>8%</option>
              <option value={0}>0%</option>
            </select>
          </FormField>

          <FormField label="税端数処理">
            <select
              value={formData.tax_rounding}
              onChange={(e) => handleFieldChange('tax_rounding', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value="floor">切り捨て</option>
              <option value="round">四捨五入</option>
              <option value="ceil">切り上げ</option>
            </select>
          </FormField>
        </div>

        <div style={{ borderTop: '2px solid #333', paddingTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '16px', marginBottom: '8px' }}>
            <span style={{ color: '#666' }}>小計:</span>
            <span style={{ fontWeight: 'bold', minWidth: '120px', textAlign: 'right' }}>
              ¥{totals.subtotal.toLocaleString()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '16px', marginBottom: '8px' }}>
            <span style={{ color: '#666' }}>値引:</span>
            <span style={{ fontWeight: 'bold', minWidth: '120px', textAlign: 'right' }}>
              -¥{formData.discount.toLocaleString()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '16px', marginBottom: '8px' }}>
            <span style={{ color: '#666' }}>消費税:</span>
            <span style={{ fontWeight: 'bold', minWidth: '120px', textAlign: 'right' }}>
              ¥{totals.taxAmount.toLocaleString()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>
            <span>合計:</span>
            <span style={{ minWidth: '120px', textAlign: 'right' }}>
              ¥{totals.total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <Modal onClose={() => setShowDeleteConfirm(false)}>
          <div style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>削除の確認</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>この請求書を削除してもよろしいですか？この操作は元に戻せません。</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: saving ? '#ccc' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showPaymentModal && (
        <PaymentModal
          onRecord={handlePaymentRecord}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
    </div>
  );
};

export default InvoiceEdit;
